import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, effect, ChangeDetectionStrategy } from '@angular/core';
import { IonDatetime, PopoverController, IonicModule, ModalController } from '@ionic/angular';
import { ResourcesEndpointEnum } from 'src/app/core/enums/resources-endpoint.enum';
import { WindowSizes } from 'src/app/core/enums/window-sizes.enum';
import { EmbedConfig } from 'src/app/core/interfaces/embed-config';
import { PowerbiService } from 'src/app/core/services/powerbi.service';
import { ResourceService } from 'src/app/core/services/resource.service';
import { ChooseSizePopoverComponent } from '../popovers/choose-size-popover/choose-size-popover.component';
import { ExportToFilePopoverComponent } from '../popovers/export-to-file-popover/export-to-file-popover.component';
import { ExportProgressModalComponent } from '../modals/export-progress-modal/export-progress-modal.component';
import { throwError, BehaviorSubject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { ReportConfig } from 'src/app/core/interfaces/report-config';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AbstractControl, FormControl } from '@angular/forms';
import { UserInterface } from 'src/app/core/interfaces/user.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PBILoaderComponent } from '../pbi-loader/pbi-loader.component';

@Component({
    selector: 'app-embedding-report',
    templateUrl: './embedding-report.component.html',
    styleUrls: ['./embedding-report.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonicModule, TranslateModule, PBILoaderComponent]
})
export class EmbeddingReportComponent implements OnInit, OnChanges, OnDestroy {
  report!: ReportConfig;
  embedConfig: any;
  public embedType!: string;
  showLoaderContainer!: boolean;
  public embedding = true;
  public loading = false;
  public windowwidth = innerWidth;
  onmobileView = false;
  windowSizes = WindowSizes;
  group: any;
  public editMode = false;
  public exportingReportProcess = false;
  public exportPercentageComplete = 0.15;
  public bookmarkaction = false;
  public playbookmarkAction = false;
  public embeddingReport = true;
  @Input() reportId!: string;
  @Input() reportType!: string;
  @Input() dashboardGroupId!: number;
  groupID!: number;
  startDate: any;
  closingDate: any;
  user!: UserInterface;
  control!: AbstractControl | null;
  internalControl!: FormControl;
  projectName: string = '';

  constructor(public popoverController: PopoverController,
    public modalController: ModalController,
    public pbiService: PowerbiService,
    private cdr: ChangeDetectorRef,
    private location: Location,
    private router: Router,
    private translateService: TranslateService,
    private toastrService: ToastrService,
    public resourceService: ResourceService) {
    effect(() => {
      this.startDate = null
      this.resourceService.startDateSubject.next(this.startDate);
    })
  }

  ngOnInit(): void {
    this.showLoaderContainer = false;
    if (this.windowwidth < this.windowSizes.MaxMobileWidth) {
      this.onmobileView = true;
    }
    this.getReport();
    this.cdr.detectChanges()
  }

  cancel(dateTime: IonDatetime): void {
    void dateTime.cancel(true);
  }

  confirm(dateTime: IonDatetime): void {
    dateTime.confirm(true);
  }

  getStartDate(date: any) {
    this.startDate = date.detail.value;
    this.resourceService.startDateSubject.next(date.detail.value)
  }

  private getReport() {
    this.showLoaderContainer = true;
    this.resourceService.get<ReportConfig>(ResourcesEndpointEnum.GET_REPORT, { groupID: this.dashboardGroupId, reportID: this.reportId })
      .subscribe({
        next: (report: ReportConfig) => {
          this.report = report;
          this.cdr.detectChanges();
        },
        complete: () => {
        }
      });
  }

  setAsFavorite() {
    this.resourceService.put<any>(ResourcesEndpointEnum.PUT_REPORT_FAVORITE, {}, { reportID: this.reportId })
      .subscribe({
        next: () => {
          this.report.UsersFavorite = !this.report.UsersFavorite;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.translateService.get('Error when toggling favorite report').subscribe(t => {
            this.toastrService.error(t);
          })
          throwError(() => err);
        },
        complete: () => {
        }
      });
  }

  showPopOver(ev: any) {
    this.presentPopover(ev);
  }

  async presentPopover(ev: any) {
    const popover = await this.popoverController.create({
      component: ChooseSizePopoverComponent,
      event: ev,
      translucent: true
    });
    await popover.present();
    const value = (await popover.onDidDismiss()).data;
    this.pbiService.setReportSize(value);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.group !== null && this.reportId !== '' && this.reportType !== '') {
      // console.log('LOADING report');
      this.loadPowerBIContent(this.reportId, this.reportType, this.dashboardGroupId);
    }
  }
  ngOnDestroy(): void { }

  loadPowerBIContent(reportId: string, reportType: string, groupID: number) {
    // console.log('Loading PowerBI content for reportId:', reportId, 'reportType:', reportType, 'groupID:', groupID);
    this.resourceService.get<EmbedConfig>(ResourcesEndpointEnum.GET_API_REPORT, { groupID: groupID, reportType: reportType, reportID: reportId }).subscribe({
      next: (data) => {
        this.embedConfig = data;
        this.cdr.detectChanges();
        // console.log('embedConfig', this.embedConfig);
      },
      error: (err) => {
        this.showLoaderContainer = false;
        this.translateService.get('Error loading the report').subscribe(t => {
          this.toastrService.error(t);
        })
        this.goBack();
      },
      complete: () => {
        this.loading = true;
        this.embedding = false;
        this.embeddingReport = this.embedConfig.EmbedType !== 'Dashboard' ? true : false;
        this.pbiService.retrieveEmbedObject(this.embedConfig, this.report);
      }
    });
  }

  async exportFile(ev: any) {
    const popover = await this.popoverController.create({
      component: ExportToFilePopoverComponent,
      event: ev,
      translucent: true
    });
    await popover.present();
    const { data: format } = await popover.onDidDismiss();
    if (!format) return;

    this.exportingReportProcess = true;
    this.cdr.detectChanges();

    const extensionMap: Record<string, string> = {
      PDF: 'pdf',
      PPTX: 'pptx',
      PNG: 'png'
    };

    // Simulated progress — increments slowly up to 90%, jumps to 100 on completion
    const progress$ = new BehaviorSubject<number>(0);
    const progressModal = await this.modalController.create({
      component: ExportProgressModalComponent,
      componentProps: { progress$, format },
      backdropDismiss: false,
      cssClass: 'export-progress-modal'
    });
    await progressModal.present();

    let simulatedProgress = 0;
    const timer = setInterval(() => {
      // Slow ramp: fast at first, then gradually decelerates near 90%
      const remaining = 90 - simulatedProgress;
      simulatedProgress += Math.max(1, Math.floor(remaining * 0.08));
      if (simulatedProgress >= 90) {
        simulatedProgress = 90;
        clearInterval(timer);
      }
      progress$.next(simulatedProgress);
    }, 600);

    this.resourceService.postBlob(
      ResourcesEndpointEnum.POST_EXPORT_REPORT,
      { format },
      { groupID: this.dashboardGroupId, reportID: this.reportId }
    ).subscribe({
      next: (response) => {
        clearInterval(timer);
        progress$.next(100);
        setTimeout(async () => {
          await progressModal.dismiss();
          this.exportingReportProcess = false;
          const blob = response.body;
          const disposition = response.headers.get('Content-Disposition');
          let fileName = `${this.report?.Title ?? 'export'}.${extensionMap[format] ?? format.toLowerCase()}`;
          if (disposition) {
            const match = disposition.match(/filename[^;=\n]*=['"]?([^'"\n;]+)['"]?/);
            if (match?.[1]) fileName = match[1].trim();
          }
          const url = URL.createObjectURL(blob!);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = fileName;
          anchor.click();
          URL.revokeObjectURL(url);
          this.translateService.get(['Export complete. The file is ready.', 'Export']).subscribe(t => {
            this.toastrService.success(t['Export complete. The file is ready.'], t['Export']);
          });
          this.cdr.detectChanges();
        }, 600);
      },
      error: () => {
        clearInterval(timer);
        progressModal.dismiss();
        this.exportingReportProcess = false;
        this.translateService.get(['Error exporting report', 'Error']).subscribe(t => {
          this.toastrService.error(t['Error exporting report'], t['Error']);
        });
        this.cdr.detectChanges();
      }
    });
  }

  goBack() {
    if (history.state.navigationId <= 1) {
      this.router.navigate(['/home']);
    } else {
      this.location.back();
    }
  }
}
